import React, { useState, useEffect } from "react";
import { Copy, X, Plus, Check } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    Tooltip,
    Checkbox,
    FormControlLabel,
    Chip,
} from "@mui/material";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Snackbar from "@mui/material/Snackbar";

export default function DeployWidgetModal({ open, onClose, agentGuid, token }) {
    const [tab, setTab] = useState(0);
    const [copied, setCopied] = useState(false);

    // EXISTING
    const [autonomousWidget, setAutonomousWidget] = useState(false);

    // NEW — WHITELIST
    const [whitelistDomains, setWhitelistDomains] = useState([]); // ["https://aaa.com"]
    const [newDomainInput, setNewDomainInput] = useState("");
    const [whitelistDirty, setWhitelistDirty] = useState(false);
    const [widgetApiKey, setWidgetApiKey] = useState(null);


    const domain = window.location.origin;

    const snippets = {
        "Plain HTML": `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <title>Sophona Widget</title>
  </head>
  <body>
    <div id="sophona-widget-root"></div>
    <link rel="stylesheet" href="https://sophona.com/widget/sophona-widget.css" />
    <script src="https://unpkg.com/react@18/umd/react.production.min.js"></script>
    <script src="https://unpkg.com/react-dom@18/umd/react-dom.production.min.js"></script>
    <script src="https://sophona.com/widget/sophona-widget.iife.js"></script>
    <script>
      SophonaChatWidget.mount("${widgetApiKey}", "${domain}");
    </script>
  </body>
</html>`,

        "React": `import React, { useEffect } from "react";

export default function App() {
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://sophona.com/widget/sophona-widget.iife.js";
    script.async = true;
    script.onload = () => {
      if (window.SophonaChatWidget) {
        window.SophonaChatWidget.mount("${widgetApiKey}", "${domain}");
      }
    };
    document.body.appendChild(script);
  }, []);

  return (
    <>
      <link
        rel="stylesheet"
        href="https://sophona.com/widget/sophona-widget.css"
      />
      <div id="sophona-widget-root"></div>
    </>
  );
}`,

        "Next.js": `// layout.tsx
export default function RootLayout({ children }) {
  return (
    <html lang="en">
      <body>
        <link rel="stylesheet" href="https://sophona.com/widget/sophona-widget.css" />
        <Script src="https://sophona.com/widget/sophona-widget.iife.js" strategy="afterInteractive" />
        <Script id="sophona-mount" strategy="afterInteractive">
          { \`
(function waitForWidget() {
  if (window.SophonaChatWidget && typeof window.SophonaChatWidget.mount === 'function') {
    window.SophonaChatWidget.mount("${widgetApiKey}", "${domain}");
  } else {
    setTimeout(waitForWidget, 50);
  }
})();
          \` }
        </Script>
        {children}
      </body>
    </html>
  );
}`
    };

    const currentCode = Object.values(snippets)[tab];

    // ---------------------------
    // COPY CODE HANDLING
    // ---------------------------
    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(currentCode);
            setCopied(true);
        } catch (err) {
            console.error("Copy failed:", err);
        }
    };

    useEffect(() => {
        if (copied) {
            const timeout = setTimeout(() => setCopied(false), 2000);
            return () => clearTimeout(timeout);
        }
    }, [copied]);

    // ---------------------------
    // FETCH AGENT (for autonomous)
    // ---------------------------
    const fetchAgent = async () => {
        if (!agentGuid) return;
       
        try {

            const queryParams = `&guid=${agentGuid}`;
            const query = "api/getdatabase?table_name=agents" + queryParams;

            const response = await fetch(query, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + token,
                },
            });

            const data = await response.json();
            const agent = data.results[0];

            setAutonomousWidget(agent.autonomous_widget === "1" ? true : false);

            
        } catch (err) {
            console.error("Error fetching agent:", err);
        }
    };
    const fetchWidget = async () => {
        if (!agentGuid) return;
        const response = await fetch(`api/deployagentwidget/by-guid?agent_guid=${agentGuid}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + token
            },
        });

        if (!response.ok) throw new Error(`Error: ${response.status}`);
        const result = await response.json();
        // NEW: fetch whitelist
        if (result.whitelist) {
            setWhitelistDomains(
                result.whitelist
                    .split(",")
                    .map((d) => d.trim())
                    .filter((d) => d.length > 0)
            );
        }
        console.log("Widget deployed:", result);

        setWidgetApiKey(result.apiKey);

      
    };

    useEffect(() => {
        fetchWidget();
        fetchAgent();
    }, [agentGuid]);

    // ---------------------------
    // AUTONOMOUS TOGGLE
    // ---------------------------
    const handleAutonomousToggle = async (checked) => {
        setAutonomousWidget(checked);


        try {
            await fetch("api/updateagent", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + token,
                },
                body: JSON.stringify({
                    guid: agentGuid,
                    autonomous_widget: checked,
                }),
            });
        } catch (err) {
            console.error("Failed to update agent autonomous widget:", err);
        }
    };

    // ---------------------------
    // WHITELIST MANAGEMENT
    // ---------------------------
    const addDomain = () => {
        const d = newDomainInput.trim();
        if (d.length === 0) return;

        setWhitelistDomains((prev) => [...prev, d]);
        setNewDomainInput("");
        setWhitelistDirty(true);
    };

    const removeDomain = (domain) => {
        setWhitelistDomains((prev) => prev.filter((d) => d !== domain));
        setWhitelistDirty(true);
    };

    const deployWhitelist = async () => {
        try {
            const response = await fetch("api/deployagentwidget", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + token,
                },
                body: JSON.stringify({
                    agent_guid: agentGuid,
                    whitelist: whitelistDomains.join(",")
                }),
            });

            if (!response.ok) throw new Error(`Error: ${response.status}`);

            const result = await response.json();
            console.log("Widget deployed:", result);

            setWhitelistDirty(false);
        } catch (err) {
            console.error("Whitelist update failed:", err);
        }
    };

    // ---------------------------
    // RENDER
    // ---------------------------
    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
            PaperProps={{ sx: { borderRadius: 3, overflow: "hidden", bgcolor: "#fafafa" } }}
        >
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 600 }}>
                Embed Sophona Chat Widget
                <X onClick={onClose} className="cursor-pointer" />
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>

                {/* Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
                    <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
                        {Object.keys(snippets).map((label, index) => (
                            <Tab key={index} label={label} />
                        ))}
                    </Tabs>
                </Box>

                {/* Code section */}
                <Box sx={{ position: "relative", bgcolor: "#1e1e1e", color: "#fff", p: 3, fontFamily: "monospace", fontSize: 14 }}>
                    <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        {currentCode}
                    </pre>

                    <button
                        onClick={handleCopy}
                        className="absolute top-2 right-2 flex items-center gap-1 bg-gray-800 text-white px-3 py-1.5 rounded-md hover:bg-gray-700 transition"
                    >
                        <Copy className="w-4 h-4" />
                        {copied ? "Copied!" : "Copy"}
                    </button>
                </Box>

                {/* Autonomous tools */}
                <Box sx={{ px: 3, py: 2, borderTop: "1px solid #eee" }}>
                    <Tooltip title="Enable autonomous agent tools." placement="right" arrow>
                        <FormControlLabel
                            control={<Checkbox checked={autonomousWidget} onChange={(e) => handleAutonomousToggle(e.target.checked)} />}
                            label="Use Autonomous Widget Tools"
                        />
                    </Tooltip>
                </Box>

                {/* ⭐ WHITELIST SECTION */}
                <Box sx={{ px: 3, py: 2, borderTop: "1px solid #eee" }}>

                    <div style={{ fontWeight: 600, marginBottom: 8 }}>Allowed Domains (Whitelist)</div>

                    {/* List of chips */}
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
                        {whitelistDomains.map((domain) => (
                            <Chip
                                key={domain}
                                label={domain}
                                onDelete={() => removeDomain(domain)}
                                sx={{ bgcolor: "#e0e0e0" }}
                            />
                        ))}
                    </Box>

                    {/* Input to add new domain */}
                    <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                        <input
                            value={newDomainInput}
                            onChange={(e) => setNewDomainInput(e.target.value)}
                            placeholder="https://example.com"
                            style={{
                                flex: 1,
                                padding: 10,
                                borderRadius: 6,
                                border: "1px solid #ccc",
                                fontFamily: "monospace",
                            }}
                        />

                        {/* '+' button ONLY if input not empty */}
                        {newDomainInput.trim().length > 0 && (
                            <button
                                onClick={addDomain}
                                className="bg-green-600 px-3 py-2 rounded-md hover:bg-green-700 transition"
                            >
                                <Plus className="w-5 h-5" />
                            </button>
                        )}
                    </Box>

                    {/* SAVE whitelist button — only when changes exist */}
                    {whitelistDirty && (
                        <Box sx={{ textAlign: "right", mt: 2 }}>
                            <button
                                onClick={deployWhitelist}
                                className="bg-blue-600  px-4 py-2 rounded-md hover:bg-blue-700 transition flex items-center gap-1"
                            >
                                <Check sx={{  bgcolor: "#1e1e1e", color: "#fff", p: 3, fontFamily: "monospace", fontSize: 14 }} className="w-4 h-4" /> Save Whitelist
                            </button>
                        </Box>
                    )}
                </Box>

            </DialogContent>

            <Snackbar
                open={copied}
                message="✅ Code copied!"
                autoHideDuration={1500}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            />
        </Dialog>
    );
}
